/** CAPTURA PLANULUI: cum se uită asistentul la ce a făcut.
 *
 *  DE CE E NEVOIE. Cifrele spun că zidul s-a mutat cu 12 cm; nu spun că a rămas
 *  o cameră fără ieșire sau că ușa a ajuns peste colț. Verificarea aia se face
 *  uitându-te la desen, și e exact ce poate face un model care vede imagini.
 *
 *  CUM. Pânza editorului e un SVG cu atribute de desen scrise pe elemente (nu
 *  cu foi de stil), deci serializat rămâne același desen. Se trece prin `Image`
 *  și `canvas` fiindcă modelul citește PNG, nu SVG.
 *
 *  CE NU INTRĂ ÎN CAPTURĂ: restul paginii. Nu e o poză a ecranului — e planul.
 *  Un `<canvas>` desenat dintr-un SVG nu poate citi nimic din afara lui.
 */
import { LAYER_ATTR } from "../viewer/tronson/editor/layers";

/** Lățimea capturii. Mai mult nu ajută: modelul citește desenul, nu tipăritura,
 *  iar fiecare pixel în plus e context plătit la fiecare tur de-acum încolo. */
const LATIME = 1400;

/** EVERY VIEW LAYER AT 100%, on the copy only. The architect may hide the
 *  furniture to read the walls; the assistant must still see the furniture,
 *  or it would place it again. A layer's wrapper carries nothing but the
 *  layer's opacity or its `display`, so taking those off leaves every
 *  element's own opacity (a dimmed route, the Design floor) as drawn. */
export function aprindeLayerele(copie: Element): void {
  for (const g of Array.from(copie.querySelectorAll(`[${LAYER_ATTR}]`))) {
    g.removeAttribute("opacity");
    g.removeAttribute("display");
  }
}

export async function capturaPlanului(): Promise<string | null> {
  const svg = document.querySelector<SVGSVGElement>(".ob-ed-panza svg");
  if (!svg) return null;
  const cadru = svg.getBoundingClientRect();
  if (!cadru.width || !cadru.height) return null;

  const copie = svg.cloneNode(true) as SVGSVGElement;
  // Mărimile trebuie scrise pe copie: originalul are `width="100%"`, iar un
  // procent fără părinte nu înseamnă nimic.
  copie.setAttribute("width", String(Math.round(cadru.width)));
  copie.setAttribute("height", String(Math.round(cadru.height)));
  copie.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  aprindeLayerele(copie);
  if (!copie.getAttribute("viewBox")) {
    copie.setAttribute("viewBox", `0 0 ${cadru.width} ${cadru.height}`);
  }
  const text = new XMLSerializer().serializeToString(copie);
  const url = URL.createObjectURL(new Blob([text], { type: "image/svg+xml" }));
  try {
    const poza = await incarca(url);
    const scara = Math.min(1, LATIME / cadru.width);
    const panza = document.createElement("canvas");
    panza.width = Math.round(cadru.width * scara);
    panza.height = Math.round(cadru.height * scara);
    const ctx = panza.getContext("2d");
    if (!ctx) return null;
    // FOND ALB. SVG-ul e transparent, iar un PNG transparent turnat peste negru
    // în vizualizatorul modelului ar fi o planșă neagră cu linii negre.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, panza.width, panza.height);
    ctx.drawImage(poza, 0, 0, panza.width, panza.height);
    return panza.toDataURL("image/png");
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

const incarca = (url: string) => new Promise<HTMLImageElement>((gata, nu) => {
  const poza = new Image();
  poza.onload = () => gata(poza);
  poza.onerror = () => nu(new Error("SVG-ul nu s-a putut încărca ca imagine"));
  poza.src = url;
});
